import { useEffect, useRef } from "react";
import { refreshWalletCustomerData } from "../../redux/http/walletThunks";
import {
  selectIsWalletCustomer,
  selectWalletEnabled,
  selectWalletRestaurantId,
} from "../../redux/selectors/walletSelectors";
import { clearWalletCustomerData } from "../../redux/slices/walletSlice";
import { useAppDispatch, useAppSelector } from "../useReduxHooks";

/** Nothing refetches the balance more often than this on tab focus. */
const REFETCH_MIN_GAP_MS = 30_000;

/**
 * Keeps the wallet slice current app-wide (mounted from BackgroundHooks), for
 * restaurants with `hasWallet` on the single-scroll home screen only
 * (`selectWalletEnabled`) — with either flag off, no wallet request is made.
 *
 * - Presets are not loaded here: they are baked into the only pages that show
 *   them (Wallet, Top Up) and hydrated there -- see useBakedWalletPresets.
 * - Balance and latest transactions load for a signed-in customer, and are
 *   cleared when the token stops being one.
 * - The wallet has no socket event, so the balance refetches when a receipt
 *   moves (an order may have spent from it) and when the tab regains focus.
 */
export const useWalletDataRefetch = () => {
  const dispatch = useAppDispatch();
  const walletEnabled = useAppSelector(selectWalletEnabled);
  const restaurantId = useAppSelector(selectWalletRestaurantId);
  const isWalletCustomer = useAppSelector(selectIsWalletCustomer);
  const token = useAppSelector((state) => state.userInfo.token);
  const currentReceiptId = useAppSelector(
    (state) => state.receipt.currentReceiptId
  );
  const payScreenReceiptState = useAppSelector(
    (state) => state.receipt.payScreenReceipt?.state
  );
  const lastRefetchRef = useRef(0);

  const enabled = walletEnabled && restaurantId !== null;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (!isWalletCustomer) {
      dispatch(clearWalletCustomerData());

      return;
    }
    lastRefetchRef.current = Date.now();
    dispatch(refreshWalletCustomerData({ restaurantId }));
  }, [
    dispatch,
    enabled,
    isWalletCustomer,
    restaurantId,
    token,
    currentReceiptId,
    payScreenReceiptState,
  ]);

  useEffect(() => {
    if (!enabled || !isWalletCustomer) {
      return;
    }

    const onVisibilityChange = () => {
      const now = Date.now();
      if (
        document.visibilityState !== "visible" ||
        now - lastRefetchRef.current < REFETCH_MIN_GAP_MS
      ) {
        return;
      }
      lastRefetchRef.current = now;
      dispatch(refreshWalletCustomerData({ restaurantId }));
    };

    document.addEventListener("visibilitychange", onVisibilityChange);

    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [dispatch, enabled, isWalletCustomer, restaurantId]);
};
